import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { validateGonkaAddress } from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWatchedAddress,
  type WatchedAddress,
} from '@gonka-wallet/storage';
import { Button, Card } from '@gonka-wallet/ui-tokens';
import { useWalletStore } from '../stores/walletStore.js';
import { useWatchedModalStore } from '../stores/watchedModalStore.js';
import { useToastStore } from '../stores/toastStore.js';
import { router } from '../router.js';

function randomUuid(): string {
  // IndexedDB on the wallet is browser-only, so crypto.randomUUID is
  // guaranteed available in all supported browsers (iOS Safari 15.4+).
  return crypto.randomUUID();
}

type ValidationError =
  | { kind: 'invalidAddress' }
  | { kind: 'duplicateWallet'; name: string }
  | { kind: 'duplicateWatched'; name: string }
  | null;

export function WatchedAddressModal(): ReactNode {
  const { t } = useTranslation();
  const isOpen = useWatchedModalStore((s) => s.isOpen);
  const close = useWatchedModalStore((s) => s.close);
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const addWatched = useWalletStore((s) => s.addWatched);
  const pushToast = useToastStore((s) => s.push);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [addressTouched, setAddressTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset fields whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setAddress('');
      setAddressTouched(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return (): void => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  const trimmedAddress = address.trim();
  const trimmedName = name.trim();

  const validationError: ValidationError = useMemo(() => {
    if (!addressTouched || trimmedAddress.length === 0) return null;
    if (!validateGonkaAddress(trimmedAddress)) return { kind: 'invalidAddress' };
    const walletMatch = wallets.find((w) => w.addresses.includes(trimmedAddress));
    if (walletMatch) return { kind: 'duplicateWallet', name: walletMatch.name };
    const watchedMatch = watched.find((w) => w.address === trimmedAddress);
    if (watchedMatch) return { kind: 'duplicateWatched', name: watchedMatch.name };
    return null;
  }, [addressTouched, trimmedAddress, wallets, watched]);

  const canSubmit =
    !submitting &&
    trimmedName.length > 0 &&
    trimmedName.length <= 64 &&
    trimmedAddress.length > 0 &&
    validationError === null &&
    validateGonkaAddress(trimmedAddress);

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const row: WatchedAddress = {
        version: 1,
        id: randomUuid(),
        name: trimmedName,
        address: trimmedAddress,
        createdAt: new Date().toISOString(),
      };
      const db = await openWalletDb();
      await saveWatchedAddress(db, row);
      addWatched(row);
      close();
      // Modal is mounted as a sibling of RouterProvider (App.tsx) so
      // useNavigate() is unavailable here — call the router directly.
      void router.navigate('/wallet');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast(msg, 'error');
      setSubmitting(false);
    }
  }

  const errorMessage: string | null = (() => {
    if (!validationError) return null;
    switch (validationError.kind) {
      case 'invalidAddress':
        return t('watchedAddress.errors.invalidAddress');
      case 'duplicateWallet':
        return t('watchedAddress.errors.duplicateWallet', {
          name: validationError.name,
        });
      case 'duplicateWatched':
        return t('watchedAddress.errors.duplicateWatched', {
          name: validationError.name,
        });
    }
  })();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="wam-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            key="wam-panel"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 360, damping: 36 }}
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col overflow-hidden rounded-t-2xl bg-bg text-text-primary shadow-2xl sm:inset-y-8 sm:left-1/2 sm:top-1/2 sm:max-h-none sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="watched-address-modal-title"
          >
            <div className="flex items-center justify-between border-b border-border-hairline bg-surface-1 px-4 py-3">
              <h2
                id="watched-address-modal-title"
                className="text-lg font-semibold"
              >
                {t('watchedAddress.add')}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label={t('watchedAddress.cancel')}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-2 hover:bg-surface-3"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-3 p-4">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
                  {t('watchedAddress.name')}
                </span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('watchedAddress.namePlaceholder')}
                  maxLength={64}
                  className="rounded-xl bg-surface-1 p-3 text-sm text-text-primary"
                  autoFocus
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
                  {t('watchedAddress.address')}
                </span>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onBlur={() => setAddressTouched(true)}
                  placeholder={t('watchedAddress.addressPlaceholder')}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="rounded-xl bg-surface-1 p-3 font-mono text-xs text-text-primary"
                />
              </label>

              {errorMessage && (
                <p className="text-xs text-neg" role="alert">
                  {errorMessage}
                </p>
              )}

              <Card className="bg-surface-1/50 text-[11px] text-text-secondary">
                {t('watchedAddress.hint')}
              </Card>

              <div className="mt-2 flex flex-col gap-2">
                <Button
                  variant="primary"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={submitting}
                  fullWidth
                >
                  {t('watchedAddress.submit')}
                </Button>
                <Button variant="ghost" onClick={close} fullWidth>
                  {t('watchedAddress.cancel')}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
